/**
 * NM (2026-10-18): the clock and the cart tier bar for the Black Friday and Cyber Monday 2026 campaign.
 *
 * Config comes from snippets/jhilke-campaign.liquid (the one source), printed as
 * <script type="application/json" data-jh-campaign-config>. Nothing here sets a price or a discount: it only decides
 * what the page shows. Shopify caches rendered HTML and the cart morphs through the section API, so the window is
 * always judged here against the UTC timestamps; the server clock is never trusted for a visible state.
 *
 * Stages: before (nothing yet), presale (pop-up message, from popup date), early (early access, only when the config
 * allows it), public, ended.
 *
 * Preview clock (QA only): ?campaign_now=2026-11-26T09:00:00Z moves this page's clock; ?campaign_now=off clears it.
 * It is read only when the config says previewOk, which Liquid sets from theme.role != 'main', so on the live
 * theme it is ignored. It changes what is displayed on this browser and nothing else.
 */
(function () {
  'use strict';
  if (window.JhCampaign) return;

  var cfg = null;
  function config() {
    if (cfg) return cfg;
    var el = document.querySelector('script[data-jh-campaign-config]');
    if (!el) return null;
    try { cfg = JSON.parse(el.textContent); } catch (e) { cfg = null; }
    if (cfg && cfg.tiers) convertTiers(cfg);
    return cfg;
  }

  // ---- other currencies (MJ 2026-10-29) ----
  // The offer is set in euro cents in snippets/jhilke-campaign.liquid. Shopify turns the minimum and the amount off of the
  // discount into the buyer's currency at checkout at its live rate, so a cart that is not in euros compares and shows the
  // converted amounts: the minimum rounded up and the amount off rounded down to a whole currency unit. The euro tiers stay
  // in cfg.tiersEur for the note under the amounts.
  var NBSP = '\u00a0';
  function activeCurrency() {
    var cur = window.Shopify && window.Shopify.currency;
    var code = cur && cur.active ? String(cur.active).toUpperCase() : 'EUR';
    var rate = cur ? Number(cur.rate) : 1;
    if (code === 'EUR' || !isFinite(rate) || rate <= 0) return null;
    return { code: code, rate: rate };
  }
  /** Euro cents to cart-currency minor units: a whole unit, rounded up (a minimum) or down (an amount off). */
  function convertCents(cents, round, cur) {
    var units = (cents / 100) * cur.rate;
    // 120 * 11.1 is 1332.0000000000002 in floating point: a hair of tolerance keeps whole results whole.
    units = round === 'up' ? Math.ceil(units - 1e-6) : Math.floor(units + 1e-6);
    return units * 100;
  }
  function convertTiers(c) {
    if (c.tiersEur) return;
    c.tiersEur = c.tiers.map(function (t) { return { min: t.min, off: t.off }; });
    var cur = activeCurrency();
    if (!cur) return;
    c.tiers = c.tiersEur.map(function (t) {
      return { min: convertCents(t.min, 'up', cur), off: convertCents(t.off, 'down', cur) };
    });
  }
  function groupSpaces(n) { return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, NBSP); }
  /** "SEK 1 330" (code, no-break space, number with no-break space thousands); a comma carries any decimals. */
  function formatLocal(cents, cur) {
    var whole = cents % 100 === 0;
    var units = Math.floor(cents / 100);
    var rest = ('0' + (cents % 100)).slice(-2);
    return cur.code + NBSP + groupSpaces(units) + (whole ? '' : ',' + rest);
  }
  function formatEuro(cents) {
    var whole = cents % 100 === 0;
    var rest = ('0' + (cents % 100)).slice(-2);
    return '\u20ac' + Math.floor(cents / 100) + (whole ? '' : ',' + rest);
  }

  var PREVIEW_KEY = 'jh_campaign_now';
  var EARLY_KEY = 'jh_bfcm_early';
  var previewState = null;

  function readPreview() {
    var c = config();
    if (!c || !c.previewOk) return null;
    try {
      var q = new URLSearchParams(window.location.search).get('campaign_now');
      if (q === 'off') { sessionStorage.removeItem(PREVIEW_KEY); return null; }
      if (q) {
        var t = Date.parse(q);
        if (isFinite(t)) { sessionStorage.setItem(PREVIEW_KEY, JSON.stringify({ ms: t, at: Date.now() })); }
      }
      var raw = sessionStorage.getItem(PREVIEW_KEY);
      if (raw) {
        var o = JSON.parse(raw);
        if (o && isFinite(o.ms) && isFinite(o.at)) return o;
      }
    } catch (e) { /* storage blocked: no preview */ }
    return null;
  }

  function now() {
    if (previewState === null) previewState = readPreview() || false;
    return previewState ? previewState.ms + (Date.now() - previewState.at) : Date.now();
  }

  function ms(key) {
    var c = config();
    var d = c && c.dates && c.dates[key];
    return d ? Date.parse(d.iso) : NaN;
  }

  function stage() {
    var c = config();
    if (!c) return 'ended';
    var t = now();
    var end = ms('close');
    var pub = ms('public');
    var early = c.early ? ms('early') : NaN;
    if (!isFinite(end) || !isFinite(pub)) return 'ended';
    if (t >= end) return 'ended';
    if (t >= pub) return 'public';
    if (isFinite(early) && t >= early) return 'early';
    var popup = ms('popup');
    if (isFinite(popup) && t >= popup) return 'presale';
    return 'before';
  }

  /** The first moment shoppers can use the sale: early access when it is on, otherwise the public start. */
  function startMs() {
    var c = config();
    return c && c.early ? ms('early') : ms('public');
  }

  function earlyFlag() {
    try { return localStorage.getItem(EARLY_KEY) === '1'; } catch (e) { return false; }
  }
  function setEarlyFlag() {
    try { localStorage.setItem(EARLY_KEY, '1'); } catch (e) { /* ignore */ }
  }

  /** Early access is shown to people who signed up on this browser; everyone sees it once the sale is public. */
  function tiersOn() {
    var s = stage();
    return s === 'public' || (s === 'early' && earlyFlag());
  }

  // ---- money ----
  function formatMoney(cents, format) {
    var local = activeCurrency();
    if (local) return formatLocal(cents, local);
    var whole = cents % 100 === 0;
    var euros = Math.floor(cents / 100);
    var rest = ('0' + (cents % 100)).slice(-2);
    function group(n, sep) { return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, sep); }
    var f = format || '€{{amount}}';
    return f.replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, name) {
      switch (name) {
        case 'amount': return whole ? group(euros, ',') : group(euros, ',') + '.' + rest;
        case 'amount_no_decimals': return group(euros, ',');
        case 'amount_with_comma_separator': return whole ? group(euros, '.') : group(euros, '.') + ',' + rest;
        case 'amount_no_decimals_with_comma_separator': return group(euros, '.');
        case 'amount_with_space_separator': return whole ? group(euros, ' ') : group(euros, ' ') + ',' + rest;
        default: return whole ? String(euros) : euros + '.' + rest;
      }
    });
  }

  // ---- the visitor's own time zone, next to the official Brussels time ----
  var BRUSSELS = 'Europe/Brussels';
  function partsOf(t, zone) {
    var o = {};
    new Intl.DateTimeFormat('en-GB', { timeZone: zone, weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
      .formatToParts(new Date(t)).forEach(function (p) { o[p.type] = p.value; });
    return o.weekday + ' ' + o.day + ' ' + o.month + ', ' + o.hour + ':' + o.minute;
  }
  /** '' when the visitor's zone shows the same clock as Brussels, otherwise the same instant in their zone. */
  function localNote(key) {
    try {
      var t = ms(key);
      var zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (!isFinite(t) || !zone) return '';
      var mine = partsOf(t, zone);
      if (mine === partsOf(t, BRUSSELS)) return '';
      var city = zone.split('/').pop().replace(/_/g, ' ');
      return ' (your time: ' + mine + ' in ' + city + ')';
    } catch (e) { return ''; }
  }
  function fillLocal() {
    var els = document.querySelectorAll('[data-jh-local]:not([data-jh-local-done])');
    for (var i = 0; i < els.length; i++) {
      els[i].textContent = localNote(els[i].getAttribute('data-jh-local'));
      els[i].setAttribute('data-jh-local-done', '');
    }
  }

  // ---- amounts and the note on the sale pages, for a cart that is not in euros ----
  function fillAmounts() {
    var cur = activeCurrency();
    if (!cur) return;
    var els = document.querySelectorAll('[data-jh-eur]:not([data-jh-eur-done])');
    for (var i = 0; i < els.length; i++) {
      var cents = parseInt(els[i].getAttribute('data-jh-eur'), 10);
      if (isFinite(cents)) {
        els[i].textContent = formatLocal(convertCents(cents, els[i].getAttribute('data-jh-round') === 'up' ? 'up' : 'down', cur), cur);
      }
      els[i].setAttribute('data-jh-eur-done', '');
    }
    if (els.length) fitBig();
    var c = config();
    var notes = document.querySelectorAll('[data-jh-eur-note]:not([data-jh-eur-done])');
    if (!c || !c.tiersEur || !c.tiersEur.length) return;
    var parts = c.tiersEur.map(function (t) { return formatEuro(t.off) + ' off from ' + formatEuro(t.min); });
    var list = parts.length > 1 ? parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1] : parts[0];
    // One visible note per page: a page can hold the banner and a sale section, each with its note. A note in a hidden
    // stage or in the announcement's screen-reader span does not count and is left as it is.
    var shown = false;
    for (var j = 0; j < notes.length; j++) {
      notes[j].textContent = 'Shown in ' + cur.code + " at today's exchange rate. The offer is set in euros: " + list + '.';
      notes[j].setAttribute('data-jh-eur-done', '');
      if (notes[j].offsetParent !== null && notes[j].getBoundingClientRect().width > 1) {
        if (shown) notes[j].hidden = true;
        shown = true;
      }
    }
  }

  // The big sale number is sized for "€25". A converted amount ("HUF 9 321") is wider, so it is scaled down to fit its
  // row. Only a number holding a converted amount is touched; a euro cart keeps the size from the CSS.
  function fitBig() {
    var nums = document.querySelectorAll('.jh-bf__num, .jh-sh__num');
    for (var i = 0; i < nums.length; i++) {
      var n = nums[i];
      if (!n.querySelector('[data-jh-eur-done]')) continue;
      n.style.fontSize = '';
      var room = n.parentElement.clientWidth;
      // Fraunces widens at smaller sizes (optical size axis), so one proportional step falls short: repeat until it fits.
      for (var k = 0; k < 4 && room > 0; k++) {
        var w = n.getBoundingClientRect().width;
        if (w <= room) break;
        n.style.fontSize = Math.floor(parseFloat(getComputedStyle(n).fontSize) * room / w * 0.97) + 'px';
      }
    }
  }

  // ---- tier bar ----
  function renderBar(bar, c) {
    var subtotal = parseInt(bar.getAttribute('data-subtotal'), 10) || 0;
    var total = parseInt(bar.getAttribute('data-total'), 10) || 0;
    var format = bar.getAttribute('data-money-format') || '€{{amount}}';
    var sig = subtotal + '|' + total + '|' + c.tiers.length;
    var msg = bar.querySelector('[data-jh-tier-msg]');
    if (!msg) return;
    if (bar.getAttribute('data-jh-sig') === sig && msg.textContent) return;

    var tiers = c.tiers;
    var reached = -1;
    for (var i = 0; i < tiers.length; i++) if (subtotal >= tiers[i].min) reached = i;
    var top = tiers[tiers.length - 1];
    var text;
    if (reached === tiers.length - 1) {
      text = 'You have ' + formatMoney(tiers[reached].off, format) + ' off. That is the top step.';
    } else {
      var next = tiers[reached + 1];
      var more = 'Add ' + formatMoney(next.min - subtotal, format) + ' more for ' + formatMoney(next.off, format) + ' off.';
      text = reached >= 0 ? 'You have ' + formatMoney(tiers[reached].off, format) + ' off. ' + more : more;
    }
    msg.textContent = text;

    var fill = bar.querySelector('[data-jh-tier-fill]');
    if (fill) fill.style.width = Math.min(100, Math.floor((subtotal * 100) / top.min)) + '%';

    var steps = bar.querySelector('[data-jh-tier-steps]');
    if (steps) {
      steps.textContent = '';
      tiers.forEach(function (t, idx) {
        var li = document.createElement('li');
        li.className = 'jh-tier__step' + (idx <= reached ? ' is-reached' : '');
        var a = document.createElement('span');
        a.className = 'jh-tier__step-off';
        a.textContent = formatMoney(t.off, format) + ' off';
        var b = document.createElement('span');
        b.className = 'jh-tier__step-min';
        b.textContent = 'from ' + formatMoney(t.min, format);
        li.appendChild(a);
        li.appendChild(b);
        if (idx <= reached) {
          var sr = document.createElement('span');
          sr.className = 'jh-tier__sr';
          sr.textContent = ' (reached)';
          li.appendChild(sr);
        }
        steps.appendChild(li);
      });
    }

    var credit = bar.querySelector('[data-jh-tier-credit]');
    if (credit) credit.textContent = 'We add your ' + c.creditPct + '% store credit ' + c.creditWaitDays + ' days after your order arrives. You can use it for ' + c.creditDays + ' days.';

    var ship = bar.querySelector('[data-jh-tier-ship]');
    if (ship) {
      ship.textContent = total >= c.freeShip
        ? 'You have free shipping.'
        : 'Add ' + formatMoney(c.freeShip - total, format) + ' more for free shipping.';
    }
    bar.setAttribute('data-jh-sig', sig);
  }

  var lastStage = null;
  var lastOn = null;
  var busy = false;

  function refresh() {
    var c = config();
    // MJ 2026-10-30: a page with no campaign config (gift cards, FAQ) can still hold converted euro amounts; fill them.
    if (!c) { fillAmounts(); return; }
    if (busy) return;
    busy = true;
    try {
      var s = stage();
      var on = tiersOn();
      var root = document.documentElement;
      if (on !== lastOn) {
        if (on) root.setAttribute('data-jh-tiers', 'on'); else root.removeAttribute('data-jh-tiers');
        lastOn = on;
      }
      if (on) {
        var bars = document.querySelectorAll('[data-jh-tier-bar]');
        for (var i = 0; i < bars.length; i++) renderBar(bars[i], c);
      }
      if (s !== lastStage) {
        lastStage = s;
        root.setAttribute('data-jh-campaign-stage', s);
        document.dispatchEvent(new CustomEvent('jh-campaign:stage', { detail: { stage: s } }));
      }
      // BF pieces (2026-10-28): which look the small pieces (bar, strip, pop-up, tier bar) wear. 'bf' = black and hot red from the pre-sale
      // start; 'cm' = electric blue from the cyber date (Sat 28 Nov 00:00 Brussels) until cyber_end (Tue 1 Dec 00:00 Brussels), then 'bf' again; none outside the sale. Same palette as the sale page.
      var look = '';
      // 2026-10-28 (orchestrator): before the sale opens the red look shows only on the Black Friday page itself ([data-jh-promo]); during the sale week (public) it is site-wide; the sales hub stays gold (next line).
      if (s === 'public' || (s === 'presale' && document.querySelector('[data-jh-promo]'))) { var cy = ms('cyber'); var cye = ms('cyber_end'); look = (isFinite(cy) && now() >= cy && (!isFinite(cye) || now() < cye)) ? 'cm' : 'bf'; }
      if (look && document.querySelector('[data-jh-hub]')) look = ''; // the sales hub wears the store palette (gold), so the bar and strip stay gold there
      if (look) root.setAttribute('data-jh-bf-look', look); else root.removeAttribute('data-jh-bf-look');
      fillLocal();
      fillAmounts();
      previewBadge();
    } finally {
      busy = false;
    }
  }

  function previewBadge() {
    var existing = document.querySelector('[data-jh-preview-badge]');
    if (!previewState) { if (existing) existing.remove(); return; }
    if (!existing) {
      existing = document.createElement('div');
      existing.setAttribute('data-jh-preview-badge', '');
      existing.setAttribute('role', 'note');
      existing.style.cssText = 'position:fixed;left:8px;bottom:8px;z-index:2147483000;background:#B5462E;color:#F4ECD9;font:600 11px/1.3 Inter,sans-serif;padding:4px 8px;border-radius:6px;pointer-events:none;max-width:70vw;';
      document.body.appendChild(existing);
    }
    existing.textContent = 'Preview clock ' + new Date(now()).toISOString().slice(0, 19) + 'Z, display only';
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () { scheduled = false; refresh(); });
  }

  window.JhCampaign = {
    config: config,
    now: now,
    ms: ms,
    startMs: startMs,
    stage: stage,
    tiersOn: tiersOn,
    earlyFlag: earlyFlag,
    setEarlyFlag: setEarlyFlag,
    formatMoney: formatMoney,
    convertCents: convertCents,
    localNote: localNote,
    refresh: refresh
  };

  function start() {
    refresh();
    if (document.body) {
      new MutationObserver(schedule).observe(document.body, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['data-subtotal', 'data-total']
      });
    }
    if (config()) setInterval(refresh, 1000);
    window.addEventListener('resize', fitBig);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitBig);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
